import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { usePasswordResetRequest } from "@api/hooks";
import { showSuccess, showError, FormLayout, FormCard, FormField, Button } from "@common";
import { FORM_IDS, forgotPasswordSchema } from "@utils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

export default function ForgotPassword() {
  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.AUTH_FORGOT_PASSWORD,
  );

  const [emailSent, setEmailSent] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const resetRequestMutation = usePasswordResetRequest();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(values) {
    try {
      if (!values.email) {
        showError("Please enter your email address");
        return;
      }

      const data = await resetRequestMutation.mutateAsync({
        email: values.email,
      });

      recordSubmitSuccess();

      setResetEmail(values.email);
      setEmailSent(true);

      // Show reset URL if in development (email service unavailable)
      if (data.reset_url) {
        showSuccess("Password reset link generated (check console)");
      } else {
        showSuccess(data.message || "Password reset email sent successfully");
      }
    } catch (error) {
      showError(getApiErrorMessage(error));
    }
  }

  if (emailSent) {
    return (
      <FormLayout>
        <FormCard title="Check Your Email">
          <div className="text-center space-y-4">
            <div className="text-sm text-smiles-gray">We've sent a password reset link to:</div>
            <div className="font-medium text-smiles-darkGray">{resetEmail}</div>
            <div className="text-sm text-smiles-lightGray">
              Please check your email and click the link to reset your password. The link will
              expire in 24 hours.
            </div>
            <div className="pt-4">
              <Button variant="outline" onClick={() => setEmailSent(false)} className="w-full">
                Send Another Email
              </Button>
            </div>
            <Link to="/login">Back to Login</Link>
          </div>
        </FormCard>
      </FormLayout>
    );
  }

  return (
    <FormLayout>
      <FormCard title="Forgot Password" onSubmit={handleSubmit(onSubmit)} {...formTrackingProps}>
        <div className="space-y-6">
          <div className="text-sm text-smiles-lightGray mb-4">
            Enter your email address and we'll send you a link to reset your password.
          </div>

          <FormField
            label="Email"
            type="email"
            placeholder="Enter your email"
            error={errors.email?.message}
            showError={!!errors.email && isSubmitting === false}
            {...register("email")}
            register={register}
            required={true}
          />

          <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
            {isSubmitting ? "Sending..." : "Send Reset Link"}
          </Button>

          <div className="text-center">
            <span className="text-sm text-smiles-lightGray">
              Remember your password?{" "}
              <Link
                to="/login"
                className="text-palette-indigo-badge font-medium hover:text-palette-indigo-heading"
              >
                Login
              </Link>
            </span>
          </div>
        </div>
      </FormCard>
    </FormLayout>
  );
}
